/**
 * Vincent B948 (Eric 2026-02-14 SPEC STRICK PROD) — Global background job
 * watcher.
 *
 * Polling toutes les 30s de `GET /api/agent/jobs?kinds=bulk-verify-factual`
 * pour détecter les jobs qui viennent de passer done|error depuis le
 * dernier check et émettre une notification toast persistante.
 *
 * Le hook est monté une seule fois dans App.js (au niveau global) pour
 * que Vincent voie la notification même s'il n'est PAS sur la page
 * Corrections factuelles quand le job se termine.
 *
 * Kinds surveillés : `bulk-verify-factual` (extensible via prop `kinds`).
 */
import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { getLayoutBootstrap, isLayoutBootWindow } from "../lib/layoutBootstrap"; // B1099

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30_000;
const STORAGE_KEY = "knoria_bg_job_watcher_seen";

const WATCHED_KINDS = "bulk-verify-factual";

function loadSeenIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

function persistSeenIds(seen) {
  try {
    // On borne à 200 IDs pour éviter que le storage grandisse indéfiniment.
    const arr = Array.from(seen).slice(-200);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch { /* silencieux */ }
}

export default function GlobalJobWatcher() {
  const { user } = useAuth();
  const { lang } = useLang();
  const isAuthenticated = !!user;
  const seenRef = useRef(loadSeenIds());
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const check = async () => {
      try {
        // B1099 (Eric 2026-09-07) — Premier check servi par l'appel agrégé
        // du gabarit ; les checks suivants (30 s) gardent /agent/jobs.
        let jobs;
        if (isLayoutBootWindow()) {
          const boot = await getLayoutBootstrap();
          jobs = boot?.agent_jobs?.jobs;
        }
        if (!jobs) {
          const { data } = await axios.get(
            `${API}/api/agent/jobs?kinds=${WATCHED_KINDS}&limit=10`,
            { withCredentials: true },
          );
          jobs = data?.jobs || [];
        }
        for (const j of jobs) {
          if (!j?.id) continue;
          if (j.status !== "done" && j.status !== "error") continue;
          if (seenRef.current.has(j.id)) continue;
          // Nouveau job terminé — notif.
          seenRef.current.add(j.id);
          const result = j.result || {};
          if (j.status === "done") {
            toast.success(
              lang === "fr"
                ? `✓ Vérification factuelle terminée : ${result.syntheses_processed || 0} synthèse(s), ${result.total_claims_detected || 0} claim(s) détecté(s). Consulte /dashboard/admin/factual-corrections.`
                : `✓ Bulk verify done: ${result.syntheses_processed || 0} synthesis, ${result.total_claims_detected || 0} claim(s) detected.`,
              { duration: 15_000 },
            );
          } else {
            toast.error(
              lang === "fr"
                ? `✗ Vérification factuelle échouée : ${j.error || "erreur inconnue"}`
                : `✗ Bulk verify failed: ${j.error || "unknown"}`,
              { duration: 15_000 },
            );
          }
        }
        persistSeenIds(seenRef.current);
      } catch (err) {
        // Silencieux : réseau intermittent, 403, etc. Pas de bruit UI.
      }
    };

    // Premier check différé 5s (laisse l'app finir son boot), puis toutes les 30s.
    const boot = setTimeout(check, 5_000);
    timerRef.current = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      clearTimeout(boot);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isAuthenticated, lang]);

  return null;
}
